import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";
import { setPanelRef } from "@/components/ui/sheet";

export interface RightPanelOptions {
  defaultWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  title?: string;
  /** Stable key used to persist the user's resized width across sessions and tabs. */
  key?: string;
}

interface RightPanelContextType {
  isOpen: boolean;
  options: RightPanelOptions;
  openPanel: (content: ReactNode, options?: RightPanelOptions) => void;
  closePanel: () => void;
  subscribeContent: (cb: () => void) => () => void;
  contentRef: React.MutableRefObject<ReactNode | null>;
  portalContainer: HTMLElement | null;
  setPortalContainer: (el: HTMLElement | null) => void;
}

const defaultOptions: RightPanelOptions = {
  defaultWidth: 35,
  minWidth: 20,
  maxWidth: 90,
};

const RightPanelContext = createContext<RightPanelContextType | undefined>(undefined);

export function useRightPanel(): RightPanelContextType {
  const ctx = useContext(RightPanelContext);
  if (!ctx) {
    // Safe no-op fallback for tests / routes that don't mount the provider.
    return {
      isOpen: false,
      options: defaultOptions,
      openPanel: () => {},
      closePanel: () => {},
      subscribeContent: () => () => {},
      contentRef: { current: null },
      portalContainer: null,
      setPortalContainer: () => {},
    };
  }
  return ctx;
}

export function RightPanelProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<RightPanelOptions>(defaultOptions);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  const contentRef = useRef<ReactNode | null>(null);
  const listenersRef = useRef<Set<(isOpen: boolean) => void>>(new Set());
  const contentListenersRef = useRef<Set<() => void>>(new Set());
  const cleanupTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isOpenRef = useRef(false);

  const notifyContentChange = useCallback(() => {
    contentListenersRef.current.forEach((cb) => cb());
  }, []);

  const subscribeContent = useCallback((cb: () => void) => {
    contentListenersRef.current.add(cb);
    return () => {
      contentListenersRef.current.delete(cb);
    };
  }, []);

  const openPanel = useCallback(
    (newContent: ReactNode, newOptions?: RightPanelOptions) => {
      if (cleanupTimerRef.current) {
        clearTimeout(cleanupTimerRef.current);
        cleanupTimerRef.current = null;
      }
      contentRef.current = newContent;
      const wasOpen = isOpenRef.current;
      if (!wasOpen) {
        isOpenRef.current = true;
        if (newOptions) setOptions({ ...defaultOptions, ...newOptions });
        setIsOpen(true);
      } else if (newOptions) {
        setOptions({ ...defaultOptions, ...newOptions });
      }
      notifyContentChange();
      listenersRef.current.forEach((l) => l(true));
    },
    [notifyContentChange],
  );

  const closePanel = useCallback(() => {
    isOpenRef.current = false;
    setIsOpen(false);
    listenersRef.current.forEach((l) => l(false));
    if (cleanupTimerRef.current) clearTimeout(cleanupTimerRef.current);
    cleanupTimerRef.current = setTimeout(() => {
      cleanupTimerRef.current = null;
      if (!isOpenRef.current) {
        contentRef.current = null;
        notifyContentChange();
      }
    }, 300);
  }, [notifyContentChange]);

  const subscribe = useCallback((listener: (isOpen: boolean) => void) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  // Bridge to <Sheet> (non-React access).
  setPanelRef({ isOpen, openPanel, closePanel, subscribe });

  return (
    <RightPanelContext.Provider
      value={{
        isOpen,
        options,
        openPanel,
        closePanel,
        subscribeContent,
        contentRef,
        portalContainer,
        setPortalContainer,
      }}
    >
      {children}
    </RightPanelContext.Provider>
  );
}
